import React, { useEffect, useRef, useState } from 'react';
import { Button, Grid, Paper, Typography } from '@mui/material';

import { CompPlexEvents } from 'utils/microfrontend/constants';

import { DEFAULT_EXPENSES_PROPERTIES } from './search-modal/constants';
import ComparableExpensesUnitsModalContainer from './search-modal/ComparableExpensesUnitsModalContainer';
import { PropertySummary } from './types';
import { ExpenseComp } from 'types/underwriting/income-approach/expenses';

interface Props {
  errorNotification: (message: string) => void;
  propertySummary: PropertySummary;
  subjectExpenses: any;
  totalSubjectOperatingExpenses?: any;
  totalSubjectOperatingExpensesNetTaxes?: any;
  comparableExpenses: ExpenseComp[];
  onAddExpenseComps: (comps: ExpenseComp[]) => void;
}

const ComparableExpensesPanel: React.FC<Props> = ({
  errorNotification,
  propertySummary,
  subjectExpenses,
  totalSubjectOperatingExpenses,
  totalSubjectOperatingExpensesNetTaxes,
  comparableExpenses,
  onAddExpenseComps,
}) => {
  const compPlexRef = useRef<HTMLElement>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  useEffect(() => {
    const compPlexElement = compPlexRef.current;

    if (!compPlexElement) {
      return;
    }

    const handleError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { error } = customEvent.detail;
      errorNotification(error.message || error);
    };
    compPlexElement.addEventListener(CompPlexEvents.ERROR, handleError);

    return () => {
      compPlexElement.removeEventListener(CompPlexEvents.ERROR, handleError);
    };
  }, [errorNotification]);

  const openSearchModal = () => {
    setIsSearchModalOpen(true);
  };

  const closeSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const handleAddExpenseComps = (comps: ExpenseComp[]) => {
    onAddExpenseComps(comps);
    closeSearchModal();
  };

  return (
    <Paper sx={{ padding: 3, marginLeft: 1, marginTop: 2 }}>
      <Grid container justifyContent="space-between" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Find Expense Comps</Typography>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={openSearchModal} sx={{ height: 36 }}>
            Search by filter
          </Button>

          {isSearchModalOpen && (
            <ComparableExpensesUnitsModalContainer
              handleClose={closeSearchModal}
              onAddExpenseComps={handleAddExpenseComps}
              filters={DEFAULT_EXPENSES_PROPERTIES}
              propertySummary={propertySummary}
              subjectExpenses={subjectExpenses}
              totalSubjectOperatingExpenses={totalSubjectOperatingExpenses}
              totalSubjectOperatingExpensesNetTaxes={totalSubjectOperatingExpensesNetTaxes}
              addedExpenseComps={comparableExpenses}
              totalOperatingExpenses={totalSubjectOperatingExpenses}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ComparableExpensesPanel;
